import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";

import AddCommentOutlined from "@mui/icons-material/AddCommentOutlined";
import ForumOutlined from "@mui/icons-material/ForumOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { NewChatDialog } from "./Dialogs";
import { chatTitle, formatTime, isChatOnline, myUnread } from "./shared";

const ScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  ...theme.scrollbarStyles
}));

// Faixa que deixa explícito que esta lista NÃO é de clientes. Dentro da tela de
// Atendimentos as duas listas ficam a um clique uma da outra, e a única coisa
// que muda é para quem a mensagem vai.
const InternalBanner = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.5),
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 193, 7, 0.14)"
      : "rgba(255, 193, 7, 0.18)",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontSize: 12
}));

const ChatList = ({ selectedChatId, onSelect, embedded = false }) => {
  const { user } = useContext(AuthContext);

  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sideTab, setSideTab] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);

  const fetchChats = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-chats");
      setChats(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // A lista se mantém sozinha pelo socket: mensagem nova reordena e acende o
  // contador mesmo com a conversa fechada.
  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const handler = payload => {
      const { action } = payload || {};

      if (action === "newChat" || action === "updateChat") {
        setChats(prev => {
          const exists = prev.some(c => c.id === payload.chat.id);
          return exists
            ? prev.map(c => (c.id === payload.chat.id ? payload.chat : c))
            : [payload.chat, ...prev];
        });
        return;
      }

      if (action === "leaveChat") {
        setChats(prev => prev.filter(c => c.id !== payload.chatId));
        return;
      }

      if (action === "newMessage") {
        const { chat } = payload;
        setChats(prev => [chat, ...prev.filter(c => c.id !== chat.id)]);
        return;
      }

      if (action === "readChat") {
        setChats(prev =>
          prev.map(c =>
            c.id === payload.chatId
              ? {
                  ...c,
                  participants: (c.participants || []).map(p =>
                    Number(p.userId) === Number(user?.id)
                      ? { ...p, unreadMessages: 0 }
                      : p
                  )
                }
              : c
          )
        );
      }
    };

    socket.on("internalChat", handler);
    return () => socket.off("internalChat", handler);
  }, [user]);

  // Zera o badge da conversa aberta sem esperar o servidor responder.
  useEffect(() => {
    if (!selectedChatId) return;
    setChats(prev =>
      prev.map(c =>
        String(c.id) === String(selectedChatId)
          ? {
              ...c,
              participants: (c.participants || []).map(p =>
                Number(p.userId) === Number(user?.id)
                  ? { ...p, unreadMessages: 0 }
                  : p
              )
            }
          : c
      )
    );
  }, [selectedChatId, user]);

  useEffect(() => {
    if (sideTab !== 1) return undefined;
    const term = search.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/internal-chats/search", {
          params: { q: term }
        });
        setSearchResults(data);
      } catch (err) {
        toastError(err);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search, sideTab]);

  const visibleChats =
    sideTab === 0
      ? chats.filter(chat =>
          chatTitle(chat, user?.id).toLowerCase().includes(search.toLowerCase())
        )
      : chats;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {embedded && (
        <InternalBanner>
          <ForumOutlined sx={{ fontSize: 15 }} />
          <span>Conversas da equipe — o cliente não vê nada daqui</span>
        </InternalBanner>
      )}

      <Box sx={{ p: 1, display: "flex", gap: 1 }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<AddCommentOutlined />}
          onClick={() => setNewChatOpen(true)}
        >
          Nova conversa
        </Button>
      </Box>

      <Tabs
        value={sideTab}
        onChange={(e, value) => {
          setSideTab(value);
          setSearch("");
        }}
        variant="fullWidth"
      >
        <Tab label="Conversas" sx={{ minHeight: 40 }} />
        <Tab label="Mensagens" sx={{ minHeight: 40 }} />
      </Tabs>

      <Box sx={{ p: 1.5 }}>
        <TextField
          placeholder={sideTab === 0 ? "Buscar conversa" : "Buscar no histórico"}
          fullWidth
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>
      <Divider />

      <ScrollArea>
        {sideTab === 1 && (
          <>
            {searching && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!searching && search.trim().length < 2 && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ p: 2, textAlign: "center" }}
              >
                Digite ao menos 2 letras para buscar.
              </Typography>
            )}
            {!searching && search.trim().length >= 2 && !searchResults.length && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ p: 2, textAlign: "center" }}
              >
                Nada encontrado.
              </Typography>
            )}
            <List disablePadding>
              {searchResults.map(result => (
                <ListItemButton
                  key={result.id}
                  onClick={() => {
                    setSideTab(0);
                    setSearch("");
                    onSelect(result.chatId);
                  }}
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          {chatTitle(result.chat, user?.id)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTime(result.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}
                      >
                        {result.sender?.name}: {result.body || result.mediaName}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {sideTab === 0 && (
          <>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!loading && !visibleChats.length && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ p: 2, textAlign: "center" }}
              >
                Nenhuma conversa ainda.
              </Typography>
            )}

            <List disablePadding>
              {visibleChats.map(chat => {
                const unread = myUnread(chat, user?.id);
                return (
                  <ListItemButton
                    key={chat.id}
                    selected={String(selectedChatId) === String(chat.id)}
                    onClick={() => onSelect(chat.id)}
                    alignItems="flex-start"
                  >
                    <ListItemAvatar>
                      <Badge
                        color="success"
                        variant="dot"
                        overlap="circular"
                        invisible={!isChatOnline(chat, user?.id)}
                      >
                        <Avatar sx={{ width: 40, height: 40, fontSize: 15 }}>
                          {chat.isGroup ? (
                            <ForumOutlined fontSize="small" />
                          ) : (
                            initialsOf(chatTitle(chat, user?.id))
                          )}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontWeight: unread ? 700 : 500 }}
                          >
                            {chatTitle(chat, user?.id)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatTime(chat.lastMessageAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            noWrap
                            sx={{ flex: 1 }}
                          >
                            {chat.lastMessage || "Sem mensagens"}
                          </Typography>
                          {unread > 0 && (
                            <Badge
                              badgeContent={unread}
                              color="error"
                              sx={{ mr: 1.5 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </ScrollArea>

      <NewChatDialog
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
        onCreated={chat => {
          setChats(prev =>
            prev.some(c => c.id === chat.id) ? prev : [chat, ...prev]
          );
          onSelect(chat.id);
        }}
      />
    </Box>
  );
};

// import nomeado renomeado para não colidir com a prop `initials` de Avatar
const initialsOf = name =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase();

export default ChatList;
